import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TabNav from '../../composants/TabNav/TabNav';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import './Annuaire.css';
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document.svg";
import { useAuth } from '../../contexts/AuthContext';
import { isClientRole, isPrestataireRole, isAdminRole } from '../../utils/roleUtils';

// Interfaces pour les données de l'API
interface Entite {
  id: number;
  nom: string;
  type: string;
}

interface LotTechnique {
  id: number;
  nom: string;
}

interface Prestataire {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
}

interface Departement {
  id: number;
  nom: string;
}

interface Contact {
  id: number;
  rang: number;
  numero: string;
  entite: Entite;
  lot_technique: LotTechnique;
  prestataire: Prestataire;
  departements: Departement[];
  // Autres champs potentiellement ajoutés pour la vue
  commandesCount?: number;
  commandesValue?: number;
  documents?: { name: string, url: string }[];
}

interface ApiResponse {
  status: string;
  data: Contact[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Modification de l'interface FilterOption pour permettre les valeurs numériques
interface FilterOption {
  label: string;
  value: string | number;
}

// Ajout des interfaces pour les nouvelles structures de données API
interface LotTechniqueApi {
  id: number;
  nom: string;
  code: string;
}

interface DepartementApi {
  id: number;
  id_region: number;
  nom: string;
  created_at: string;
  updated_at: string;
}

interface LotDepartementApi {
  id: number;
  id_departement: number;
  created_at: string;
  updated_at: string;
  departement: DepartementApi;
  contrats: any[]; // Structure simplifiée pour ce cas d'usage
}

interface LotDepartementsResponse {
  [key: string]: LotDepartementApi;
}

const Annuaire: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [acheteurs, setAcheteurs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Nouvel état pour stocker les données des API
  const [lotsTechniques, setLotsTechniques] = useState<LotTechniqueApi[]>([]);
  const [lotsDepartements, setLotsDepartements] = useState<LotDepartementApi[]>([]);

  // État pour la pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  });

  // États pour les filtres
  const [departementOptions, setDepartementOptions] = useState<FilterOption[]>([]);
  const [lotOptions, setLotOptions] = useState<FilterOption[]>([{ label: "Toutes les catégories", value: "" }]);
  const [rangOptions, setRangOptions] = useState<FilterOption[]>([{ label: "Tous", value: "" }]);
  const [nomOptions, setNomOptions] = useState<FilterOption[]>([{ label: "Toutes les entreprises", value: "" }]);

  const [selectedDepartement, setSelectedDepartement] = useState<string | number>(75);
  const [selectedLot, setSelectedLot] = useState<string | number>("");
  const [selectedRang, setSelectedRang] = useState<string>("");
  const [selectedNom, setSelectedNom] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Utilisation de roleUtils pour déterminer le rôle de l'utilisateur
  const isClient = isClientRole(user?.role || '');
  const isPrestataire = isPrestataireRole(user?.role || '');
  const isAdmin = isAdminRole(user?.role || '');

  // Définir les onglets en fonction du rôle
  const tabs = isClient
    ? ["Annuaire des prestataires", "Annuaire des acheteurs"]
    : ["Annuaire des acheteurs", "Annuaire des prestataires"];

  // Fonction pour déterminer si l'onglet actuel est "Annuaire des acheteurs"
  const isAcheteurTab = () => {
    if (isClient && activeTab === 1) return true;
    if (!isClient && activeTab === 0) return true;
    return false;
  };

  const handleRowClick = (row: any) => {
    navigate(`/contact/${row.prestataire?.id}`);
  };

  const getColumns = () => {
    const baseColumns = [
      {
        field: 'lot_technique',
        header: 'Catégories',
        renderCell: (_: any, row: Contact) => (
          <div className="categorie-cell">
            {row.lot_technique?.nom || '-'}
          </div>
        )
      },
      {
        field: 'departements',
        header: 'Départements',
        renderCell: (_: any, row: Contact) => {
          // Si aucun département n'est sélectionné, afficher tous les départements comme avant
          if (!selectedDepartement) {
            return (
              <div className="departement-cell">
                {row.departements && row.departements.length > 0
                  ? row.departements.map(dep => dep.nom).join(', ')
                  : '-'}
              </div>
            );
          }

          // Sinon, chercher le département sélectionné dans la liste des départements du contact
          const selectedDep = row.departements?.find(dep => dep.id === Number(selectedDepartement));

          return (
            <div className="departement-cell">
              {selectedDep ? selectedDep.nom : '-'}
            </div>
          );
        }
      },
      {
        field: 'rang',
        header: 'Rang',
        renderCell: (_: any, row: Contact) => (
          <div className="rang-cell">
            {row.rang || '-'}
          </div>
        )
      },
      {
        field: 'entite',
        header: 'Entreprise',
        renderCell: (_: any, row: Contact) => (
          <div className="entreprise-cell">
            {row.entite?.nom || '-'}
          </div>
        )
      },
      {
        field: 'prestataire',
        header: 'Information',
        renderCell: (_: any, row: Contact) => (
          <div className="contact-info-cell">
            <div className="contact-name">
              {row.prestataire ? `${row.prestataire.prenom} ${row.prestataire.nom}` : '-'}
            </div>
            <div className="contact-email">{row.prestataire?.email || '-'}</div>
            <div className="contact-phone">{row.prestataire?.telephone || '-'}</div>
          </div>
        )
      },
      {
        field: 'documents',
        header: 'Documents',
        renderCell: (_: any, row: Contact) => {
          if (!row.documents || !row.documents.length) {
            return <span className="no-documents">Aucun document</span>;
          }
          return (
            <div className="documents-cell">
              <button className="documents-toggle documents-icon-counter">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#DA7027" />
                  <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="bold" fill="white">
                    {row.documents.length}
                  </text>
                </svg>
              </button>
              <div className="documents-popup">
                <h4 className="documents-popup-title">Documents</h4>
                <div className="documents-popup-list">
                  {row.documents.map((doc: { name: string, url: string }, index: number) => (
                    <a
                      key={index}
                      href={doc.url}
                      className="document-item"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DocumentsIcon className="document-icon" />
                      <span className="document-name">{doc.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          );
        }
      },
      {
        field: 'Evaluation',
        header: 'Evaluation',
        renderCell: (_: any, row: Contact) => (
          <div className="contact-info-cell">
          </div>
        )
      },
    ];

    return baseColumns;
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    fetchContacts(1, query, selectedDepartement, selectedLot, selectedRang, selectedNom);
  };

  const handleExport = () => {
    console.log('Exporting...');
  };

  const handleFilter = () => {
    console.log('Filtering...');
  };

  // Fonctions de gestion des changements de filtres
  const handleDepartementChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    // Convertir en nombre si ce n'est pas une chaîne vide
    const departementValue = value === "" ? "" : Number(value);
    setSelectedDepartement(departementValue);
    fetchContacts(1, searchQuery, departementValue, selectedLot, selectedRang, selectedNom);
  };

  const handleLotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    // Convertir en nombre si ce n'est pas une chaîne vide
    const lotValue = value === "" ? "" : Number(value);
    setSelectedLot(lotValue);
    fetchContacts(1, searchQuery, selectedDepartement, lotValue, selectedRang, selectedNom);
  };

  const handleRangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRang(value);
    fetchContacts(1, searchQuery, selectedDepartement, selectedLot, value, selectedNom);
  };

  const handleNomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedNom(value);
    fetchContacts(1, searchQuery, selectedDepartement, selectedLot, selectedRang, value);
  };

  // Nouvelle fonction pour récupérer les lots techniques depuis l'API
  const fetchLotsTechniques = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error("Utilisateur non authentifié");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lots-technique`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      // Vérifier la structure de la réponse
      console.log("Structure des lots techniques:", result);

      // Déterminer si nous avons un tableau ou un objet avec une propriété data
      const lotsData = Array.isArray(result) ? result :
        (result.data && Array.isArray(result.data)) ? result.data :
          [];

      setLotsTechniques(lotsData);

      // Mise à jour des options de lots techniques pour le dropdown
      const options = lotsData.map((lot: LotTechniqueApi) => ({
        label: `${lot.id} - ${lot.nom}`,
        value: lot.id
      }));
      options.sort((a, b) => a.label.localeCompare(b.label));
      options.unshift({ label: "Tous les lots", value: "" });
      setLotOptions(options);
    } catch (error: any) {
      console.error('Erreur lors du chargement des lots techniques:', error);
      setError(`Impossible de charger les lots techniques: ${error.message}`);
    }
  };

  // Nouvelle fonction pour récupérer les départements depuis l'API
  const fetchLotsDepartements = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error("Utilisateur non authentifié");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/departements`, // URL corrigée
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log("Structure des départements:", result);

      // Extraire les données de départements selon la structure de la réponse
      const departementsData = result.data || [];

      // Vérifier si nous avons bien un tableau de départements
      if (Array.isArray(departementsData)) {
        // Créer les options pour le dropdown des départements
        const options = departementsData.map(dep => ({
          label: `${dep.id} - ${dep.nom}`,
          value: dep.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        // Ne pas ajouter l'option "Tous les départements"
        setDepartementOptions(options);

        // Vérifier si le département 75 existe dans la liste
        // const paris = options.find(opt => opt.value === 75);
        // if (paris) {
        //   setSelectedDepartement(75);
        // } else if (options.length > 0) {
        //   // Si le département 75 n'existe pas, sélectionner le premier de la liste
        //   setSelectedDepartement(options[0].value);
        // }
      } else {
        console.error('Format de données de départements inattendu:', departementsData);
        throw new Error('Format de données de départements inattendu');
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des départements:', error);
      setError(`Impossible de charger les départements: ${error.message}`);
    }
  };

  // Extraire les rangs et noms d'entreprises à partir des données de contacts
  const extractRangAndNomOptions = (data: Contact[]) => {
    // Créer des Sets pour éviter les doublons
    const rangs = new Set<string>();
    const noms = new Set<string>();

    data.forEach(contact => {
      // Extraire les rangs
      if (contact.rang) {
        rangs.add(contact.rang.toString());
      }

      // Extraire les noms d'entreprise
      if (contact.entite?.nom) {
        noms.add(contact.entite.nom);
      }
    });

    // Définir les rangs possibles avec leurs libellés explicites
    const rangOptions = [
      { label: "Tous", value: "" },
      { label: "Titulaire de rang 1", value: "1" },
      { label: "Titulaire de rang 2", value: "2" },
      { label: "Titulaire de rang 3", value: "3" },
      { label: "Référencé", value: "4" }
    ];

    const nomOptions = Array.from(noms)
      .sort()
      .map(nom => ({ label: nom, value: nom }));

    // Ajouter l'option "Tous" au début de la liste des noms
    nomOptions.unshift({ label: "Toutes les entreprises", value: "" });

    return { rangOptions, nomOptions };
  };

  // Fonction pour récupérer les contacts depuis l'API
  const fetchContacts = async (
    page: number = 1,
    search: string = "",
    departement: string | number = "",
    lot: string | number = "",
    rang: string = "",
    nom: string = ""
  ) => {
    try {
      setLoading(true);

      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();

      // Ajouter la page pour la pagination
      queryParams.append('page', page.toString());

      // Ajouter les filtres s'ils sont définis
      if (search) queryParams.append('search', search);
      if (departement !== "") queryParams.append('departement', departement.toString());
      if (lot !== "") queryParams.append('lot', lot.toString());
      if (rang) queryParams.append('rang', rang);
      if (nom) queryParams.append('nom', nom);

      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error("Utilisateur non authentifié");
      }

      // Requête à l'API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/prestataire/annuaire?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result: ApiResponse = await response.json();

      if (result.status !== "success") {
        throw new Error("La requête a échoué");
      }

      // Trier les contacts par rang dans l'ordre croissant
      const sortedContacts = [...result.data].sort((a, b) => {
        // Gérer les valeurs nulles ou undefined
        if (a.rang === null || a.rang === undefined) return 1;
        if (b.rang === null || b.rang === undefined) return -1;
        return a.rang - b.rang;
      });

      // Mettre à jour les contacts avec les données triées
      setContacts(sortedContacts);

      // Mettre à jour la pagination
      setPagination({
        currentPage: result.pagination.current_page,
        lastPage: result.pagination.last_page,
        perPage: result.pagination.per_page,
        total: result.pagination.total
      });

      // Extraire et mettre à jour uniquement les options de rangs et noms
      if (page === 1) {
        const { rangOptions: newRangOptions, nomOptions: newNomOptions } = extractRangAndNomOptions(result.data);
        setRangOptions(newRangOptions);
        setNomOptions(newNomOptions);
      }

      setError(null);
    } catch (error: any) {
      console.error('Erreur lors du chargement des données:', error);
      setError(`Impossible de charger les contacts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les données des acheteurs
  const fetchAcheteurs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error("Utilisateur non authentifié");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteur/annuaire`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      setAcheteurs(result.data || []);
      setError(null);
    } catch (error: any) {
      console.error('Erreur lors du chargement des acheteurs:', error);
      setError(`Impossible de charger les acheteurs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer le changement d'onglet
  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  useEffect(() => {
    // Si nous sommes sur l'onglet des acheteurs, récupérer les acheteurs
    if (isAcheteurTab()) {
      fetchAcheteurs();
    } else {
      // Sinon, récupérer les lots techniques, départements et contacts comme avant
      fetchLotsTechniques();
      fetchLotsDepartements();
      fetchContacts(1, "", 75, "", "", "");
    }
  }, [activeTab, user]);

  // Fonction pour gérer le changement de page
  const handlePageChange = (page: number) => {
    // Mise à jour de la page courante et récupération des données pour cette page
    fetchContacts(page, searchQuery, selectedDepartement, selectedLot, selectedRang, selectedNom);
  };

  // Rendu des filtres dropdown
  const renderFilterDropdowns = () => (
    <div className="annuaire-filters">

      <div className="filter-dropdown">
        <label htmlFor="lot-filter">Catégorie</label>
        <select
          id="lot-filter"
          value={selectedLot}
          onChange={handleLotChange}
        >
          {lotOptions.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="departement-filter">Département</label>
        <select
          id="departement-filter"
          value={selectedDepartement}
          onChange={handleDepartementChange}
        >
          {departementOptions.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>



      <div className="filter-dropdown">
        <label htmlFor="rang-filter">Types</label>
        <select
          id="rang-filter"
          value={selectedRang}
          onChange={handleRangChange}
        >
          {rangOptions.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="nom-filter">Nom entreprise</label>
        <input
          type="text"
          id="nom-filter"
          value={selectedNom}
          onChange={handleNomChange}
          placeholder="Saisir un nom d'entreprise"
        />
      </div>
    </div>
  );

  // Fonction modifiée pour définir les colonnes des acheteurs
  const getAcheteursColumns = () => {
    return [
      {
        field: 'nom',
        header: 'Nom',
        renderCell: (_: any, acheteur: any) => (
          <div className="acheteur-nom-cell">
            {acheteur.nom || acheteur.organisme?.nom || '-'}
          </div>
        )
      },

    ];
  };

  // Rendu des acheteurs
  const renderAcheteurs = () => {
    if (loading) {
      return <div className="loading">Chargement des acheteurs...</div>;
    }

    if (error) {
      return <div className="error">{error}</div>;
    }

    return (
      <TableauDeBord
        data={acheteurs}
        columns={getAcheteursColumns()}
        searchPlaceholder="Rechercher un acheteur"
        typeFilters={false}
        onSearch={handleSearch}
        onExport={handleExport}
        onFilter={handleFilter}
        isSelectable={true}
        onRowClick={() => {}}
        showActionsColumn={false}
        pagination={{
          currentPage: 1,
          lastPage: 1,
          total: acheteurs.length,
          onPageChange: () => { }
        }}
      />
    );
  };

  return (
    <div className="annuaire-page">
      <h1 className="page-title">Annuaire</h1>
      <div className="annuaire-content">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {isAcheteurTab() ? (
          // Afficher la liste des acheteurs
          renderAcheteurs()
        ) : (
          <>
            {/* Afficher les filtres et le tableau pour l'annuaire des prestataires */}
            {renderFilterDropdowns()}
            {loading ? (
              <div className="loading">Chargement des contacts...</div>
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
              <TableauDeBord
                data={contacts}
                columns={getColumns()}
                searchPlaceholder="Trouver un Contact"
                typeFilters={false}
                onSearch={handleSearch}
                onExport={handleExport}
                onFilter={handleFilter}
                isSelectable={true}
                onRowClick={handleRowClick}
                showActionsColumn={false}
                pagination={{
                  currentPage: pagination.currentPage,
                  lastPage: pagination.lastPage,
                  total: pagination.total,
                  onPageChange: handlePageChange
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Annuaire;